import * as Encryption from '@/core/encryption/Encryption';
import encryptPassword = Encryption.encryptPassword;
import {AxiosWebApiClient} from '@/core/webapi/WebApiClient';
import {WebFormModel} from "capitalist-shared/src/core/model";
import {RemoteRequestState} from "capitalist-shared/src/core/remote";
import httpAxiosApi from "capitalist-shared/src/core/axios.errors.handle";

export enum AuthenticationErrorStates {
  Success = 1,
  FailEncrypt,
  FailGetAttributes,
  WrongPassword,
  SystemError
}

interface ModelResponse {
  values: any;
  errors: any;
}

interface LoginDataResponse {
  model: any
  profile: any
  results: Record<string, any>
  oauth: any
  returnUrl: string | null
}

export class AuthResults {
  state: AuthenticationErrorStates = AuthenticationErrorStates.Success;

  protected data: LoginDataResponse | null = null;

  hasModel(): boolean {
    return !!this.data && ('model' in this.data);
  }

  getResponseData(): LoginDataResponse {
    return this.data as LoginDataResponse;
  }

  getModel(): ModelResponse | null {
    return this.hasModel() ? (this.data as any).model : null;
  }

  setResultData(result: any) {
    this.data = result;
    return this;
  }

  getOauthToken() {
    return this.data && Object.prototype.hasOwnProperty.call(this.data, 'oauth') && this.data.oauth ? this.data.oauth.token : null
  }

  // getResultUrl(): string {
  //     return (this.data as any).results.returnUrl ? (this.data as any).results.returnUrl : /*this.data.results.redirect : */'/finance';
  // }
}

// Public Key
export class SecurityAttributes {
  modulus: string;

  exponent: string;

  constructor(modulus: string, exponent: string) {
    this.modulus = modulus;
    this.exponent = exponent;
  }
}

export class Authenticator {
  client: AxiosWebApiClient;

  constructor(client?: AxiosWebApiClient) {
    this.client = client || new AxiosWebApiClient(`${process.env.VUE_APP_API_HOST}`);
  }

  protected obfuscatePasswordForVerification(password?: string): string {
    return password && password.length > 0 ? '*' : '';
  }

  async getAttributes(username: string, password: string, captchaCode = '') {
    return this.client.call('/webapi/auth/attributes', {
      m: {
        username,
        password: this.obfuscatePasswordForVerification(password),
        captchaCode,
      },
    }).then((result) => result.data.attributes);
  }

  async google2fa(username: string, token: string, code: string, oauth = false) {
    const $result: AuthResults = new AuthResults();

    return this.client.call('/webapi/auth/login/googlecode', {
      call: 'googlecode',
      m: {
        username,
        code,
        token,
        oauth
      },
    }, {}, 'googlecode')
      .then((result) => {
        $result.state = AuthenticationErrorStates.Success;
        $result.setResultData(result.data);
        return $result;
      })
      .catch((err) => {
        $result.state = AuthenticationErrorStates.SystemError;
        throw err;
      });
  }

  async login(username: string,
              password: string,
              captchaCode = '',
              statistics = '',
              redirect = '',
              oauth = false) {
    const $result: AuthResults = new AuthResults();

    let attributes: any = null;

    for (let i = 0; i < 3; i++) {
      attributes = await this.getAttributes(username, password, captchaCode)
        .then((attributes: any) => attributes)
        .catch((err) => {
          $result.state = AuthenticationErrorStates.FailGetAttributes;
          // $result.state = AuthenticationErrorStates.SystemError;
          throw err;
        });
      if (attributes) break;
    }

    if (!attributes) throw new Error('Failed to get attributes.');

    const encryptedPassword = encryptPassword(password, attributes.modulus, attributes.exponent);

    if (!encryptedPassword) {
      $result.state = AuthenticationErrorStates.FailEncrypt;
      throw new Error('Failed to encrypt password.');
    }

    return this.client.call('/webapi/auth/login', {
      m: {
        username,
        password: encryptedPassword,
        stat: statistics,
        passto: redirect,
        captchaCode,
        oauth
      },
    })
      .then((result) => {
        $result.state = AuthenticationErrorStates.Success;
        $result.setResultData(result.data);
        return $result;
      })
      .catch((err) => {
        $result.state = AuthenticationErrorStates.SystemError;
        throw err;
      });
  }
}


export class CheckAuthTokenModel extends WebFormModel {
  checkingStatus = new RemoteRequestState();
  redirectingUrl: string | null = null;

  debugDisableRedirect = false;

  checkToken() {
    this.errors.clear();
    this.checkingStatus.loadStart();
    return httpAxiosApi.post('/webapi/auth/oauth/token/token', {
      call: 'token'
    })
      .then((response) => {
        this.checkingStatus.loadEnd();
        return response.data;
      })
      .catch((err) => {
        this.checkAxiosError(err);
        this.checkingStatus.loadError();
        throw err;
      });
  }

  redirectWithToken(url: string, token: string | null = null) {
    if (!token) {
      this.redirectingUrl = url;
      // console.log('redirectWithToken', url);
      if (!this.debugDisableRedirect)
        window.location.href = url;
      return;
    }
    const u = new URL(url);
    u.searchParams.set('token', token);
    this.redirectingUrl = u.toString();
    // console.log('redirectWithToken', u.toString());
    if (!this.debugDisableRedirect)
      window.location.href = u.toString();
  }
}
